/**
 * Сгенерирован из design-tokens/tokens.json — руками не править.
 * Обновить: npm run tokens:generate
 *
 * Значение роли — путь в палитре, а не цвет. Тёмная тема получается
 * подстановкой того же пути в DSDarkColors.
 *
 * Выгрузка из Figma 2026-09-02, файл co7KxelJDeXNtsPN3RxdUj.
 */
import type { DSColorsKeys } from '../colors';

type AliasNode = DSColorsKeys | { [key: string]: AliasNode };

export const SEMANTIC_ALIASES = {
  action: {
    accent: {
      bg: 'primary.600',
      bgHover: 'primary.700',
      text: 'white',
      textOnSurface: 'primary.600',
      border: 'primary.600',
      borderHover: 'primary.700',
      textOnSurfaceHover: 'primary.700',
      bgOutline: 'white',
      bgMinor: 'white',
    },
    neutral: {
      bg: 'gray.100',
      bgHover: 'gray.200',
      text: 'gray.800',
      bgSelected: 'primary.100',
      bgSelectedHover: 'primary.200',
      bgOutlineHover: 'gray.300',
      bgOutline: 'gray.200',
      bgMinor: 'white',
    },
    bgDisabled: 'gray.100',
    textDisabled: 'gray.400',
    critical: { text: 'red.500', bgHover: 'red.500', textHover: 'white' },
    additional: {
      bg: 'primary.100',
      bgHover: 'primary.200',
      text: 'primary.800',
    },
    super: {
      bg: 'purple.600',
      bgHover: 'purple.700',
      bgSecondary: 'purple.100',
      bgSecondaryHover: 'purple.200',
      text: 'purple.800',
      border: 'purple.300',
      bgOutline: 'white',
    },
    success: {
      bgSelected: 'green.600',
      bgSelectedHover: 'green.700',
      bg: 'green.100',
      bgHover: 'green.200',
      text: 'green.800',
    },
    warning: {
      bgSelected: 'orange.600',
      bgSelectedHover: 'orange.700',
      bg: 'orange.100',
      bgHover: 'orange.200',
      text: 'orange.800',
    },
    error: {
      bgSelected: 'red.600',
      bgSelectedHover: 'red.700',
      bg: 'red.100',
      bgHover: 'red.200',
      text: 'red.800',
    },
  },
  indication: {
    info: {
      bg: 'primary.100',
      border: 'primary.200',
      bgMain: 'primary.500',
      textSecondary: 'primary.600',
      text: 'primary.800',
      bgSubtle: 'primary.50',
      borderStrong: 'primary.400',
    },
    success: {
      bg: 'green.100',
      border: 'green.200',
      bgMain: 'green.500',
      textSecondary: 'green.600',
      text: 'green.800',
      bgSubtle: 'green.50',
      borderStrong: 'green.400',
    },
    warning: {
      bg: 'orange.100',
      border: 'orange.200',
      bgMain: 'orange.500',
      textSecondary: 'orange.600',
      text: 'orange.800',
      bgSubtle: 'orange.50',
      borderStrong: 'orange.400',
    },
    error: {
      bg: 'red.100',
      border: 'red.200',
      bgMain: 'red.500',
      textSecondary: 'red.600',
      text: 'red.800',
      bgSubtle: 'red.50',
      borderStrong: 'red.400',
    },
    super: {
      bg: 'purple.100',
      border: 'purple.200',
      bgMain: 'purple.500',
      textSecondary: 'purple.600',
      text: 'purple.800',
      bgSubtle: 'purple.50',
      borderStrong: 'purple.400',
    },
    neutral: {
      bg: 'gray.100',
      bgMain: 'gray.500',
      textSecondary: 'gray.600',
      text: 'gray.800',
      bgSubtle: 'gray.50',
      borderStrong: 'gray.400',
    },
    textOnMain: 'white',
    bgTertiary: 'gray.100',
    bgDisabled: 'gray.100',
    textDisabled: 'gray.400',
    bgMinor: 'white',
  },
  text: {
    default: 'gray.800',
    defaultHover: 'gray.900',
    secondary: 'gray.600',
    secondaryHover: 'gray.700',
    tertiary: 'gray.500',
    placeholder: 'gray.400',
    disabled: 'gray.400',
    onColored: 'white',
    onInverse: 'white',
    success: 'green.500',
    successHover: 'green.600',
    warning: 'orange.500',
    warningHover: 'orange.600',
    error: 'red.500',
    errorHover: 'red.600',
    accent: 'primary.500',
    accentHover: 'primary.600',
  },
  bg: {
    page: 'gray.50',
    surface: 'white',
    surfaceHover: 'gray.50',
    surface2: 'gray.50',
    surface2Hover: 'gray.100',
    surfaceActive: 'gray.200',
    surfaceSubtle: 'gray.50',
    selected: 'primary.50',
    selectedHover: 'primary.100',
    overlay: 'blackTransparent.50',
    inverse: 'black',
    inverseHover: 'gray.800',
    inverseSelected: 'gray.700',
    onMedia: 'blackTransparent.50',
    onMediaHover: 'blackTransparent.70',
    selectedStrong: 'primary.100',
  },
  border: {
    default: 'gray.200',
    subtle: 'gray.100',
    control: 'gray.300',
    controlHover: 'gray.500',
    focus: 'primary.500',
    success: 'green.500',
    successFocus: 'green.600',
    warning: 'orange.500',
    warningFocus: 'orange.600',
    error: 'red.500',
    errorFocus: 'red.600',
    inverse: 'black',
    disabled: 'gray.300',
    surface: 'gray.300',
  },
  focus: {
    ringAccent: 'primary.100',
    ringNeutral: 'gray.100',
    ringSuccess: 'green.100',
    ringWarning: 'orange.100',
    ringError: 'red.100',
    ringSuper: 'purple.100',
  },
  icon: {
    default: 'gray.800',
    secondary: 'gray.500',
    secondaryHover: 'gray.600',
    disabled: 'gray.400',
    neutral: 'gray.400',
    onColored: 'white',
    onInverse: 'white',
    onControl: 'white',
    onControlDisabled: 'gray.300',
    accent: 'primary.500',
    info: 'primary.400',
    success: 'green.500',
    warning: 'orange.400',
    error: 'red.500',
    critical: 'red.500',
    super: 'purple.400',
  },
  field: {
    bg: 'white',
    bgDisabled: 'gray.100',
    bgPrefix: 'gray.100',
    icon: 'gray.400',
    iconHover: 'gray.600',
    bgPrefixSubtle: 'gray.50',
  },
  control: {
    bg: 'white',
    bgHover: 'gray.50',
    bgChecked: 'primary.600',
    bgCheckedHover: 'primary.700',
    bgDisabled: 'gray.100',
    borderChecked: 'primary.600',
    borderCheckedHover: 'primary.700',
    track: 'gray.400',
    trackHover: 'gray.500',
    trackDisabled: 'gray.300',
  },
  scrollbar: { thumb: 'gray.200', thumbHover: 'gray.500' },
  avatar: { bg: 'gray.100', placeholder: 'gray.300', borderGroup: 'white' },
  link: {
    accent: { text: 'primary.500', textHover: 'primary.600' },
    neutral: { text: 'gray.500', textHover: 'gray.600' },
    success: { text: 'green.500', textHover: 'green.600' },
    warning: { text: 'orange.500', textHover: 'orange.600' },
    error: { text: 'red.500', textHover: 'red.600' },
    super: { text: 'purple.500', textHover: 'purple.600' },
    textDisabled: 'gray.400',
    neutralPressed: 'gray.800',
  },
  tab: {
    neutral: { bg: 'gray.100', bgHover: 'gray.200', bgSelected: 'white' },
    accent: { bg: 'white', bgHover: 'gray.100', bgSelected: 'primary.50' },
    border: 'gray.300',
    borderSelected: 'primary.600',
    textHover: 'primary.500',
    textSelected: 'primary.600',
    textDisabled: 'gray.400',
    borderTrack: 'gray.200',
    textSelectedHover: 'primary.700',
  },
  loader: { bg: 'white', indicator: 'primary.600', trail: 'primary.100' },
  menu: { border: 'gray.200' },
  chip: { bg: 'gray.100', bgHover: 'gray.200' },
  switch: {
    trackDisabled: 'gray.200',
    thumb: 'white',
    thumbDisabled: 'gray.50',
  },
} as const satisfies {
  [key: string]: AliasNode;
};
